import { Directive, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import moment from 'moment';
import { AnnualPlanGraphComponent } from './annual-plan-graph.component';
import { EffectiveWeek } from '../../merger/annual-plan-data-merger';
import { HorizontalScrollDirective } from '../../directive/horizontal-scroll';
import { Week } from "../../../common/models/week";
import { AnnualPlan } from "../../../common/models/annual-plan";


@Directive({
  selector: '[baseGraph]'  // Use this selector to apply the directive
})
export abstract class BaseGraph {
    public static CHART_WEEK_WIDTH = 30;
    public static CHART_HEIGHT = 330;
    public static CHART_SMALL_HEIGHT = 80;

    @Input() public annualPlan: AnnualPlan = {} as AnnualPlan;
    @Input() public weeks: Week[] | EffectiveWeek[] | any = [];
    @Input() public viewMode = 'big';
    @Input() public clickedWeek: Week | undefined;
    @Input() public showMonths = false;

    @Output() public graphScrollPosition = new EventEmitter();

    @ViewChild(HorizontalScrollDirective, {static: false})
    public scroll: HorizontalScrollDirective | undefined;
    @ViewChild('scrollable', {static: false})
    public graphInnerDiv: ElementRef | undefined;

    public chartSvgPoints: any = [];
    public chartWidth: number = 1;
    public clickedSelector: any;
    public months: any;
    public dayInMonths: any[] = [];
    public monthFactor: any;
    public annualPlanWeekNumberStart: number = 1;
    public chartViewBox: string = '0 0 0 0';
    public isLoading = true;
    public displayedNumberOfDays: number = 7;

    public getChartHeight() {
        return 'small' === this.viewMode ?
            BaseGraph.CHART_SMALL_HEIGHT :
            BaseGraph.CHART_HEIGHT
        ;
    }

    public onScroll(event: any) {
        this.graphScrollPosition.emit(this.scroll!.scrollLeftPosition);
    }

    public loadGraph(replace?: boolean) {
        if (!this.weeks) {
            return;
        }

        if (replace) {
          // Réinitialiser les données pour éviter la duplication
          this.months = [];
          this.dayInMonths = [];
        }

        this.displayedNumberOfDays = this.weeks.length * 7;
        this.monthFactor = this.displayedNumberOfDays / 100;

        if (this.annualPlan.startedAt) {
            this.months = this.getMonthsForDates(this.annualPlan.startedAt, this.annualPlan.weeksLength);
            let i = 0;

            let sumMonthWidth = 0;
            for (const month of this.months) {
                const daysInMonth = 0 === i ? month.diff : month.date.daysInMonth();
                const previousSumInMonth = sumMonthWidth;
                const monthWidth = daysInMonth / this.monthFactor;
                sumMonthWidth += monthWidth;

                this.dayInMonths.push({
                    month: month.date,
                    days: daysInMonth,
                    width: sumMonthWidth >= 100 ? Math.abs(100 - previousSumInMonth) : monthWidth
                });

                i++;
            }
        }

        // this.monthFactor = 100 / this.annualPlan.weeksLength * 7;

        this.annualPlanWeekNumberStart = 1;
        this.chartWidth = this.weeks.length * BaseGraph.CHART_WEEK_WIDTH ?? 1; // in px
        this.chartViewBox = '0 0 ' + this.chartWidth + ' ' + this.getChartHeight();

        this.calculateSvgPoints();

        // Add real week number if necessary
        if (this.showMonths) {
            for (const week of this.weeks) {
                const dateStart = moment(this.annualPlan.startedAt).local();
                const nextWeek = dateStart.clone().add(week.weekNumber! - 1, 'weeks');

                week['dateStart'] = nextWeek;
                week['nextWeek'] = nextWeek;
                week['realWeekNumber'] = nextWeek.week();
                week['realWeekYear'] = nextWeek.weekYear();
                week['firstDayOfWeek'] = nextWeek.clone().startOf('isoWeek');
                week['lastDayOfWeek'] = nextWeek.clone().endOf('isoWeek');
            }
        }

    }

    protected onChanges(changes: SimpleChanges) {
        if (this.clickedWeek) {
            this.clickedSelector = this.clickedWeek.id;
        }

        if (changes['scrollPosition'] && this.graphInnerDiv) {
            this.graphInnerDiv.nativeElement.scrollLeft = changes['scrollPosition'].currentValue;
        }
    }

    protected calculateSvgPoints() {
        this.chartSvgPoints = []; // reset array

        this.chartSvgPoints.push(0);
        this.chartSvgPoints.push(this.getChartHeight());

        if (!this.weeks) {
            return;
        }

        for (let i = 0; i < this.weeks.length; i++) {
            this.chartSvgPoints.push((i * AnnualPlanGraphComponent.CHART_WEEK_WIDTH) + 15);
            this.chartSvgPoints.push(
                Math.abs(this.weeks[i].intensityPercent * this.getChartHeight() / 100 - this.getChartHeight())
            );
        }

        this.chartSvgPoints.push(this.chartWidth);
        this.chartSvgPoints.push(this.getChartHeight());
    }

    protected getMonthsForDates(dateStart: moment.MomentInput, weeksLength: moment.DurationInputArg1) {
        dateStart = moment(dateStart).startOf('week');

        const dateEnd = moment(this.annualPlan.startedAt).add(weeksLength, 'weeks');
        const timeValues = [];

        while (dateEnd >= dateStart || dateStart.format('M') === dateEnd.format('M')) {
            const oneMoreMonth = dateStart.clone();
            const oneMoreMonthLastDay = oneMoreMonth.clone().endOf('month');

            timeValues.push({
                date: oneMoreMonth,
                diff: oneMoreMonthLastDay.diff(oneMoreMonth.clone(), 'days') + 1,
                diffLast: parseInt(oneMoreMonth.format('D'), 10)
            });
            dateStart.add(1, 'month');
        }

        return timeValues;
    }
}

export default BaseGraph;
